/**
 * sticky-atc-component
 *
 * Sticky Add-to-Cart bar. Integrates with Bijou's event bus and cart drawer.
 *
 * - IntersectionObserver on add-to-cart-component → show / hide
 * - ThemeEvents.variantUpdate → sync variant ID, availability, price
 * - Delegates submit to the main product-form-component <form>
 * - Falls back to own fetch + CartAddEvent if the main form is not found
 */

import { ThemeEvents } from '@theme/events';

class StickyAtcComponent extends HTMLElement {
  #variantId        = null;
  #variantAvailable = true;
  #observer         = null;
  #isVisible        = false;
  #isSubmitting     = false;
  #transitionDuration = 320;  // ms — must match CSS transition
  #animationTimeout   = null;
  static #ANIMATION_DURATION = 2000;

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  connectedCallback() {
    this.#variantId        = this.dataset.variantId || null;
    this.#variantAvailable = this.dataset.available !== 'false';

    this.#setupIntersectionObserver();
    this.#listenVariantUpdates();
    this.#bindAddButton();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  // ─── Visibility ───────────────────────────────────────────────────────────

  #findAnchor() {
    const section = document.querySelector('.shopify-section:has(product-form-component)');
    return section?.querySelector('add-to-cart-component')
      ?? document.querySelector('add-to-cart-component');
  }

  #setupIntersectionObserver() {
    const anchor = this.#findAnchor();
    if (!anchor) { this.#setupScrollFallback(); return; }

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        // Only show once the main ATC has scrolled above the viewport,
        // not while it's still below the fold (e.g. mobile page load).
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        scrolledPast ? this.#show() : this.#hide();
      },
      { threshold: 0, rootMargin: '0px' }
    );
    this.#observer.observe(anchor);
  }

  #setupScrollFallback() {
    const threshold = parseInt(this.dataset.scrollThreshold, 10) || 300;
    const onScroll = () => { window.scrollY > threshold ? this.#show() : this.#hide(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  #show() {
    if (this.#isVisible) return;
    this.#isVisible = true;
    this.removeAttribute('aria-hidden');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { this.setAttribute('data-visible', ''); });
    });
  }

  #hide() {
    if (!this.#isVisible) return;
    this.#isVisible = false;
    this.removeAttribute('data-visible');
    setTimeout(() => {
      if (!this.#isVisible) this.setAttribute('aria-hidden', 'true');
    }, this.#transitionDuration);
  }

  // ─── Variant sync ─────────────────────────────────────────────────────────

  #listenVariantUpdates() {
    document.addEventListener(ThemeEvents.variantUpdate, (event) => {
      const { resource: variant, data } = event.detail ?? {};
      if (!variant) return;

      if (
        data?.productId &&
        this.dataset.productId &&
        String(data.productId) !== String(this.dataset.productId)
      ) return;

      this.#variantId        = String(variant.id);
      this.#variantAvailable = Boolean(variant.available);

      this.#syncButton(variant.available);
      this.#syncPriceFromVariant(variant);
    });
  }

  #syncButton(available) {
    const btn = this.querySelector('[data-ref="stickyAddBtn"]');
    if (!btn) return;

    btn.disabled = !available;

    const textEl = btn.querySelector('[data-ref="stickyBtnText"]');
    if (textEl) {
      textEl.textContent = available
        ? this.dataset.addText    || 'Add to cart'
        : this.dataset.soldOutText || 'Sold out';
    }

    const iconEl = btn.querySelector('[data-ref="stickyBtnIcon"]');
    if (iconEl) iconEl.hidden = !available;
  }

  #syncPriceFromVariant(variant) {
    const priceEl   = this.querySelector('[data-ref="stickyBtnPrice"]');
    const compareEl = this.querySelector('[data-ref="stickyBtnCompare"]');
    const priceWrap = this.querySelector('[data-ref="stickyBtnPriceWrap"]');
    if (!priceEl) return;

    const fmt = (cents) => window.Shopify?.formatMoney
      ? Shopify.formatMoney(cents, window.Theme?.moneyFormat || '{{amount}}')
      : (cents / 100).toFixed(2);

    priceEl.textContent = fmt(variant.price);

    if (compareEl) {
      const onSale = variant.compare_at_price > variant.price;
      compareEl.textContent = onSale ? fmt(variant.compare_at_price) : '';
      compareEl.hidden = !onSale;
    }

    if (priceWrap) {
      priceWrap.hidden = false;
      const divider = priceWrap.nextElementSibling;
      if (divider?.classList.contains('satc__btn-divider')) divider.hidden = false;
    }
  }

  // ─── Cart submission ──────────────────────────────────────────────────────

  #bindAddButton() {
    const btn = this.querySelector('[data-ref="stickyAddBtn"]');
    if (!btn) return;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (btn.disabled || this.#isSubmitting) return;
      this.#handleAddToCart(btn);
    });
  }

  async #handleAddToCart(btn) {
    this.#isSubmitting = true;
    this.#animateAdded(btn);

    const mainForm = document.querySelector('product-form-component')?.querySelector('form') ?? null;

    if (mainForm) {
      this.#setLoading(btn, true);
      const clear = () => { this.#setLoading(btn, false); this.#isSubmitting = false; };

      document.addEventListener(ThemeEvents.cartUpdate, clear, { once: true });
      const timeout = setTimeout(() => {
        document.removeEventListener(ThemeEvents.cartUpdate, clear);
        clear();
      }, 8000);

      try {
        if (typeof mainForm.requestSubmit === 'function') {
          mainForm.requestSubmit();
        } else {
          mainForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      } catch {
        clearTimeout(timeout);
        clear();
      }
      return;
    }

    // Fallback: own fetch when main form is not present
    try {
      await this.#ownFetch(btn);
    } finally {
      this.#isSubmitting = false;
    }
  }

  async #ownFetch(btn) {
    this.#setLoading(btn, true);
    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map(el => el.dataset.sectionId || el.getAttribute('data-section-id') || el.sectionId)
        .filter(Boolean);

      // Read qty from the main form if available, otherwise default to 1
      const qty = parseInt(
        document.querySelector('product-form-component quantity-selector-component [name="quantity"]')?.value,
        10
      ) || 1;

      const body = new FormData();
      body.append('id', this.#variantId);
      body.append('quantity', qty);
      if (sectionIds.length) {
        body.append('sections', sectionIds.join(','));
        body.append('sections_url', window.location.pathname);
      }

      const url = window.Theme?.routes?.cart_add_url ?? '/cart/add.js';
      const response = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || `Cart error ${response.status}`);
      }

      const data = await response.json();
      document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate, {
        bubbles: true,
        detail: {
          resource: data,
          sourceId: this.#variantId,
          data: {
            source:    'sticky-atc',
            itemCount: data.quantity,
            productId: this.dataset.productId,
            sections:  data.sections ?? {},
          },
        },
      }));
    } catch (error) {
      console.error('[sticky-atc] Cart add failed:', error);
    } finally {
      this.#setLoading(btn, false);
    }
  }

  // ─── Animation ────────────────────────────────────────────────────────────

  #animateAdded(btn) {
    clearTimeout(this.#animationTimeout);
    btn.classList.add('atc-added');
    this.#animationTimeout = setTimeout(() => {
      btn.classList.remove('atc-added');
    }, StickyAtcComponent.#ANIMATION_DURATION);
  }

  #setLoading(btn, loading) {
    btn.disabled = loading || !this.#variantAvailable;
    btn.setAttribute('aria-busy', String(loading));
  }
}

if (!customElements.get('sticky-atc-component')) {
  customElements.define('sticky-atc-component', StickyAtcComponent);
}